import React from "react";
import { Box } from "@mui/material";
import { ColorsConstants } from "../cssUtils/constants";
import typography from "../cssUtils/typography";

const DONE_COLOR = "#1FA45A";

/**
 * StepTabs — horizontal step indicator with an underline per step.
 *
 * Props:
 *  - steps: Array<{ label: string, disabled?: boolean }>
 *  - current: number — index of the current step (earlier steps show as done)
 *  - onStepClick: (index) => void — called for enabled steps other than the current one
 */
const StepTabs = ({ steps = [], current = 0, onStepClick, sx = {} }) => (
  <Box
    role="list"
    sx={{ display: "flex", flexWrap: "wrap", ...sx }}
  >
    {steps.map((step, index) => {
      const isCurrent = index === current;
      const isDone = index < current;
      const clickable = !step.disabled && !isCurrent && Boolean(onStepClick);
      const color = isCurrent
        ? ColorsConstants.primaryBackgroundColor
        : isDone
          ? DONE_COLOR
          : "#E2E5EC";

      return (
        <Box
          key={step.label}
          role="listitem"
          aria-current={isCurrent ? "step" : undefined}
          aria-disabled={step.disabled || undefined}
          tabIndex={clickable ? 0 : -1}
          onClick={() => clickable && onStepClick(index)}
          onKeyDown={(e) => {
            if (clickable && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onStepClick(index);
            }
          }}
          sx={{
            ...typography.bodyLarge,
            flex: 1,
            minWidth: 110,
            px: 1.5,
            py: 1.25,
            borderBottom: `3px solid ${color}`,
            color: isCurrent
              ? ColorsConstants.primaryBackgroundColor
              : isDone
                ? "#1C2333"
                : "#6A7284",
            fontWeight: isCurrent ? 600 : 400,
            cursor: clickable ? "pointer" : "default",
            opacity: step.disabled ? 0.6 : 1,
            "&:focus-visible": {
              outline: `2px solid ${ColorsConstants.primaryBackgroundColor}`,
              outlineOffset: 2,
            },
          }}
        >
          {`${index + 1}. ${step.label}`}
        </Box>
      );
    })}
  </Box>
);

export default StepTabs;
